import React, { useState, useEffect } from "react";

const DataViewerPopup = ({
  isOpen,
  onClose,
  data,
  initialExpandAll = false,
}) => {
  const [viewMode, setViewMode] = useState("summary"); // 'summary', 'detailed', 'raw'
  const [expandedSections, setExpandedSections] = useState({});

  // Effect to expand all sections if initialExpandAll is true
  useEffect(() => {
    if (initialExpandAll && isOpen) {
      expandAllSections(data);
    }
  }, [initialExpandAll, data, isOpen]);

  // Function to recursively generate paths for all object properties and set them as expanded
  const expandAllSections = (obj, basePath = "") => {
    if (!obj) return;

    const newExpandedSections = { ...expandedSections };

    const processObject = (obj, path) => {
      if (Array.isArray(obj)) {
        newExpandedSections[path] = true;
        if (obj.length > 0 && typeof obj[0] === "object") {
          obj.forEach((item, index) => {
            processObject(item, `${path}[${index}]`);
          });
        }
      } else if (typeof obj === "object" && obj !== null) {
        newExpandedSections[path] = true;
        Object.keys(obj).forEach((key) => {
          const newPath = path ? `${path}.${key}` : key;
          processObject(obj[key], newPath);
        });
      }
    };

    processObject(obj, basePath);
    setExpandedSections(newExpandedSections);
  };

  if (!isOpen) return null;

  // Toggle expanded state for a section
  const toggleSection = (path) => {
    setExpandedSections((prev) => ({
      ...prev,
      [path]: !prev[path],
    }));
  };

  // Format data for display based on the view mode
  const formatData = () => {
    if (viewMode === "raw") {
      // Raw mode just returns the full data structure
      return JSON.stringify(data, null, 2);
    }

    if (viewMode === "detailed") {
      // Detailed view with sample items from arrays
      const formatWithSamples = (obj, path = "") => {
        if (!obj) return "null";

        if (Array.isArray(obj)) {
          const isExpanded = expandedSections[path];

          if (obj.length === 0) return "[]";

          if (!isExpanded) {
            return `Array: ${obj.length} items [click to view]`;
          }

          // For expanded arrays, show the first 2 items and a sample in the middle
          const samples = [];

          // Add first 2 items
          for (let i = 0; i < Math.min(2, obj.length); i++) {
            samples.push(
              `[${i}]: ${formatWithSamples(obj[i], `${path}[${i}]`)}`
            );
          }

          // Add a sample from the middle if the array is long enough
          if (obj.length > 4) {
            const midIndex = Math.floor(obj.length / 2);
            samples.push(`... ${obj.length - 4} more items ...`);
            samples.push(
              `[${midIndex}]: ${formatWithSamples(
                obj[midIndex],
                `${path}[${midIndex}]`
              )}`
            );
          }

          // Add last 2 items
          if (obj.length > 2) {
            for (let i = Math.max(2, obj.length - 2); i < obj.length; i++) {
              samples.push(
                `[${i}]: ${formatWithSamples(obj[i], `${path}[${i}]`)}`
              );
            }
          }

          return samples.join("\n");
        }

        if (typeof obj === "object" && obj !== null) {
          const isExpanded = expandedSections[path];
          const keys = Object.keys(obj);

          if (keys.length === 0) return "{}";

          if (!isExpanded) {
            return `Object: ${keys.length} properties [click to view]`;
          }

          const formatted = [];

          for (const key of keys) {
            const newPath = path ? `${path}.${key}` : key;
            formatted.push(`${key}: ${formatWithSamples(obj[key], newPath)}`);
          }

          return formatted.join("\n");
        }

        return JSON.stringify(obj);
      };

      return formatWithSamples(data);
    }

    // Summary view - replace arrays with length info
    const createSummary = (obj, path = "") => {
      if (!obj) return "null";

      if (Array.isArray(obj)) {
        const isExpanded = expandedSections[path];

        if (obj.length === 0) return "[]";

        if (!isExpanded) {
          // Show just the array length
          return `[Array: ${obj.length} items] [click to view]`;
        }

        // If expanded, create summary of first item
        const firstItem = obj[0];
        if (typeof firstItem === "object" && firstItem !== null) {
          const sampleKeys = Object.keys(firstItem).slice(0, 4);
          const sample = {};
          sampleKeys.forEach((key) => {
            sample[key] = firstItem[key];
          });

          if (Object.keys(firstItem).length > 4) {
            sample["..."] = `(${
              Object.keys(firstItem).length - 4
            } more fields)`;
          }

          return `Sample item format: ${JSON.stringify(sample)}`;
        }

        // For primitive arrays, show first 3 items
        return `[${obj
          .slice(0, 3)
          .map((x) => JSON.stringify(x))
          .join(", ")}${
          obj.length > 3 ? `, ... (${obj.length - 3} more)` : ""
        }]`;
      }

      if (typeof obj === "object" && obj !== null) {
        const isExpanded = expandedSections[path];
        const keys = Object.keys(obj);

        if (keys.length === 0) return "{}";

        const result = {};

        for (const key of keys) {
          const newPath = path ? `${path}.${key}` : key;
          result[key] = createSummary(obj[key], newPath);
        }

        return isExpanded
          ? result
          : `{Object: ${keys.length} props} [click to view]`;
      }

      return obj;
    };

    return JSON.stringify(createSummary(data), null, 2);
  };

  // Handle click on a line with [click to view] to expand it
  const handleTextClick = (e) => {
    const text = e.target.textContent;
    if (text.includes("[click to view]")) {
      // Extract path from the selected text's data attribute
      const lineText = e.target.textContent;
      const path = e.target.dataset.path || "";
      toggleSection(path);
    }
  };

  // Add path data attributes to the lines with [click to view]
  const enhanceTextWithPathData = (text) => {
    if (!text) return [];

    const lines = text.split("\n");
    return lines.map((line, idx) => {
      // Try to extract path from the line
      let path = "";
      if (line.includes("[click to view]")) {
        // The path is everything before the colon
        const match = line.match(/^(\S+):/);
        if (match) {
          path = match[1];
        }
      }

      return (
        <div
          key={idx}
          data-path={path}
          className={
            line.includes("[click to view]")
              ? "cursor-pointer hover:text-blue-400"
              : ""
          }
        >
          {line}
        </div>
      );
    });
  };

  // Function to expand all sections (can be called from button)
  const expandAll = () => {
    expandAllSections(data);
  };

  // Function to collapse all sections
  const collapseAll = () => {
    setExpandedSections({});
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4 overflow-auto">
      <div className="bg-gray-800 rounded-lg max-w-5xl w-full max-h-[95vh] sm:max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-lg sm:text-xl font-bold text-gray-200">
            Master Data Structure
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-700 text-gray-400 hover:text-white"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="p-4 overflow-auto flex-1">
          <div className="flex flex-col gap-3 mb-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              <button
                className={`px-3 py-1 text-xs sm:text-sm rounded ${
                  viewMode === "summary"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-700 text-gray-300"
                }`}
                onClick={() => setViewMode("summary")}
              >
                Summary View
              </button>
              <button
                className={`px-3 py-1 text-xs sm:text-sm rounded ${
                  viewMode === "detailed"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-700 text-gray-300"
                }`}
                onClick={() => setViewMode("detailed")}
              >
                Detailed View
              </button>
              <button
                className={`px-3 py-1 text-xs sm:text-sm rounded ${
                  viewMode === "raw"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-700 text-gray-300"
                }`}
                onClick={() => setViewMode("raw")}
              >
                Raw Data
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                className="px-3 py-1 bg-indigo-600 text-white rounded text-xs sm:text-sm"
                onClick={expandAll}
              >
                Expand All
              </button>
              <button
                className="px-3 py-1 bg-indigo-700 text-white rounded text-xs sm:text-sm"
                onClick={collapseAll}
              >
                Collapse All
              </button>
              <button
                className="px-3 py-1 bg-blue-600 text-white rounded text-xs sm:text-sm"
                onClick={() => {
                  // Copy to clipboard
                  const dataStr = JSON.stringify(data, null, 2);
                  navigator.clipboard
                    .writeText(dataStr)
                    .then(() => alert("Data copied to clipboard!"))
                    .catch((err) => console.error("Failed to copy:", err));
                }}
              >
                Copy to Clipboard
              </button>
            </div>
          </div>

          <div
            className="bg-gray-900 p-4 rounded-lg overflow-auto"
            style={{ maxHeight: "calc(80vh - 100px)" }}
            onClick={handleTextClick}
          >
            <pre className="text-xs text-gray-300 whitespace-pre-wrap">
              {enhanceTextWithPathData(formatData())}
            </pre>
          </div>

          <div className="mt-4 text-sm text-gray-400 flex items-start gap-2">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              ></path>
            </svg>
            <p>
              Click on sections labeled "[click to view]" to expand/collapse
              them. Use the view modes to see different levels of detail.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataViewerPopup;
